import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import { useAuth } from './AuthContext';
import { toast } from 'sonner';

// Import custom hooks
import { useChatWebSocket } from '../hooks/useChatWebSocket';
import { useChatNotifications } from '../hooks/useChatNotifications';
import { useChatMessages } from '../hooks/useChatMessages';
import { useCallManager } from '../hooks/useCallManager';

var ChatContext = createContext(null);
var API = process.env.REACT_APP_BACKEND_URL;

var DEFAULT_NOTIFICATION_PREFS = {
  enabled: true,
  sound_enabled: true,
  vibrate_enabled: true,
  all_messages: true,
  direct_messages: true,
  group_messages: true,
  support_messages: true,
};

export function ChatProvider(_ref) {
  var children = _ref.children;
  var auth = useAuth();
  var token = auth.token;
  var user = auth.user;

  var _conv = useState(null); var activeConversation = _conv[0]; var setActiveConversation = _conv[1];
  var _typing = useState({}); var typingUsers = _typing[0]; var setTypingUsers = _typing[1];
  var _online = useState({}); var onlineUsers = _online[0]; var setOnlineUsers = _online[1];
  var _prefs = useState(DEFAULT_NOTIFICATION_PREFS); var notificationPrefs = _prefs[0]; var setNotificationPrefs = _prefs[1];
  var messageHandlersRef = useRef({});
  var activeConversationRef = useRef(null);
  var conversationsRef = useRef([]);

  useEffect(function() { activeConversationRef.current = activeConversation; }, [activeConversation]);

  // Notification hooks (for chat message sounds only)
  var notifs = useChatNotifications(user, notificationPrefs);

  // Message hooks
  var msgHooks = useChatMessages(token, user ? user.user_id : null);

  useEffect(function() { conversationsRef.current = msgHooks.conversations; }, [msgHooks.conversations]);

  // === WebSocket message handler ===
  // callManagerRef allows the handler to call callManager methods without circular dependency
  var callManagerRef = useRef(null);

  var handleWebSocketMessage = useCallback(function(data) {
    switch (data.type) {
      case 'pong':
        break;

      case 'message_deleted':
        msgHooks.removeMessage(data.message_id);
        break;

      case 'new_message':
        var newMsg = data.message;
        msgHooks.addMessage(newMsg, activeConversationRef.current ? activeConversationRef.current.conversation_id : null);
        var convo = conversationsRef.current.find(function(c) { return c.conversation_id === newMsg.conversation_id; });
        notifs.showBrowserNotification(newMsg, convo);
        Object.values(messageHandlersRef.current).forEach(function(handler) { handler(newMsg); });
        break;

      case 'typing':
        setTypingUsers(function(prev) {
          var next = Object.assign({}, prev);
          next[data.conversation_id] = data.is_typing ? { user_id: data.user_id, user_name: data.user_name } : null;
          return next;
        });
        if (data.is_typing) {
          setTimeout(function() {
            setTypingUsers(function(prev) { var n = Object.assign({}, prev); n[data.conversation_id] = null; return n; });
          }, 5000);
        }
        break;

      case 'user_status':
        setOnlineUsers(function(prev) { var n = Object.assign({}, prev); n[data.user_id] = data.status === 'online'; return n; });
        break;

      case 'online_users':
        if (data.status) setOnlineUsers(function(prev) { return Object.assign({}, prev, data.status); });
        break;

      case 'message_read':
        msgHooks.markMessageRead(data.message_id, data.read_by, data.read_at);
        break;

      case 'message_delivered':
        msgHooks.markMessageDelivered(data.message_id, data.delivered_to || data.user_id, data.delivered_at);
        break;

      case 'message_updated':
        if (data.message_id && data.updates) msgHooks.updateMessage(data.message_id, data.updates);
        break;

      case 'force_logout':
        toast.error(`You were logged out by ${data.by || 'an administrator'}`);
        localStorage.removeItem('token'); sessionStorage.removeItem('token');
        setTimeout(() => { window.location.href = '/staff/login'; }, 800);
        break;

      // === CALL EVENTS → Delegated to useCallManager ===
      case 'incoming_call':
      case 'call_initiated':
      case 'call_accepted':
      case 'call_ringing':
      case 'call_participants':
      case 'call_invited':
      case 'participant_left':
      case 'call_rejected':
      case 'call_failed':
      case 'call_ended':
        if (callManagerRef.current) callManagerRef.current.handleWsEvent(data);
        // Also notify VideoCall component for cleanup on call_ended/call_accepted
        if (data.type === 'call_ended' && messageHandlersRef.current.callEnded) {
          messageHandlersRef.current.callEnded(data);
        }
        if (data.type === 'call_accepted' && messageHandlersRef.current.callAccepted) {
          messageHandlersRef.current.callAccepted(data);
        }
        if (data.type === 'participant_left' && messageHandlersRef.current.participantLeft) {
          messageHandlersRef.current.participantLeft(data);
        }
        break;

      case 'webrtc_signal':
        if (messageHandlersRef.current.webrtcSignal) messageHandlersRef.current.webrtcSignal(data);
        break;

      case 'call_force_mute':
        window.dispatchEvent(new CustomEvent('bbc:force-mute', { detail: data }));
        break;

      case 'call_media_mode':
        // Other side switched between media server and direct connection → follow
        if (callManagerRef.current && data.media_mode) callManagerRef.current.setMediaMode(data.media_mode);
        break;

      case 'screen_share_started':
      case 'screen_share_stopped':
        if (messageHandlersRef.current.screenShare) messageHandlersRef.current.screenShare(data);
        break;

      case 'notification':
        if (messageHandlersRef.current.newNotification) messageHandlersRef.current.newNotification(data);
        break;

      case 'new_customer_registered':
        if (messageHandlersRef.current.newCustomerRegistered) messageHandlersRef.current.newCustomerRegistered(data);
        break;

      case 'new_loan_application':
        if (messageHandlersRef.current.newLoanApplication) messageHandlersRef.current.newLoanApplication(data);
        break;

      case 'new_expense_created':
        if (messageHandlersRef.current.newExpenseCreated) messageHandlersRef.current.newExpenseCreated(data);
        break;

      case 'expense_qa_new_entry':
        if (messageHandlersRef.current.expenseQaNewEntry) messageHandlersRef.current.expenseQaNewEntry(data);
        break;

      default:
        break;
    }
  }, [msgHooks.addMessage, notifs.showBrowserNotification, msgHooks.markMessageRead, msgHooks.updateMessage]);

  // WebSocket
  var ws = useChatWebSocket(token, handleWebSocketMessage);

  // Call manager (uses sendWsMessage from WebSocket)
  var callManager = useCallManager(ws.sendWsMessage, function(signal) {
    // signals fetched over REST (poll) when the WebSocket was down
    if (messageHandlersRef.current.webrtcSignal) messageHandlersRef.current.webrtcSignal(signal);
  });
  callManagerRef.current = callManager;

  // Messaging
  var sendMessage = useCallback(function(conversationId, content, messageType, extras) {
    return ws.sendWsMessage({
      type: 'message',
      conversation_id: conversationId,
      content: content,
      message_type: messageType || 'text',
      ...(extras || {})
    });
  }, [ws.sendWsMessage]);

  var sendTyping = useCallback(function(conversationId, isTyping) {
    ws.sendWsMessage({ type: 'typing', conversation_id: conversationId, is_typing: isTyping !== false });
  }, [ws.sendWsMessage]);

  var requestOnlineStatus = useCallback(function(conversationId) {
    ws.sendWsMessage({ type: 'get_online_users', conversation_id: conversationId });
  }, [ws.sendWsMessage]);

  var registerHandler = useCallback(function(key, handler) {
    messageHandlersRef.current[key] = handler;
    return function() { delete messageHandlersRef.current[key]; };
  }, []);

  var saveNotificationPrefs = useCallback(function(prefs) {
    setNotificationPrefs(prefs);
    try { localStorage.setItem('chat_notification_prefs', JSON.stringify(prefs)); } catch (e) { /* noop */ }
    fetch(API + '/api/chat/notification-preferences', {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + token },
      body: JSON.stringify(prefs),
    }).catch(function() {});
  }, [token]);

  // Load notification preferences
  useEffect(function() {
    if (!token) return;
    fetch(API + '/api/chat/notification-preferences', { headers: { Authorization: 'Bearer ' + token } })
      .then(function(r) { return r.ok ? r.json() : null; })
      .then(function(data) { if (data) { var merged = Object.assign({}, DEFAULT_NOTIFICATION_PREFS, data); setNotificationPrefs(merged); try { localStorage.setItem('chat_notification_prefs', JSON.stringify(merged)); } catch (e) { /* noop */ } } })
      .catch(function() {});
  }, [token]);

  var markConversationRead = useCallback(function(conversationId) {
    if (!token || !conversationId) return;
    msgHooks.setConversations(function(prev) {
      return prev.map(function(c) { return c.conversation_id === conversationId ? Object.assign({}, c, { unread_count: 0 }) : c; });
    });
    fetch(API + '/api/chat/conversations/' + conversationId + '/read', {
      method: 'POST',
      headers: { Authorization: 'Bearer ' + token }
    }).catch(function() {});
  }, [token, msgHooks.setConversations]);

  // Fetch conversations on token
  useEffect(function() { if (token) msgHooks.fetchConversations(); }, [token, msgHooks.fetchConversations]);

  // Refetch on WS reconnect
  var prevConnectedRef = useRef(false);
  useEffect(function() {
    if (ws.isConnected && !prevConnectedRef.current) msgHooks.fetchConversations();
    prevConnectedRef.current = ws.isConnected;
  }, [ws.isConnected, msgHooks.fetchConversations]);

  // Periodic refresh
  useEffect(function() {
    if (!token) return;
    var refresh = function() { if (document.visibilityState === 'visible') msgHooks.fetchConversations(); };
    document.addEventListener('visibilitychange', refresh);
    window.addEventListener('focus', refresh);
    var interval = setInterval(refresh, 30000);
    return function() {
      document.removeEventListener('visibilitychange', refresh);
      window.removeEventListener('focus', refresh);
      clearInterval(interval);
    };
  }, [token, msgHooks.fetchConversations]);

  var value = {
    isConnected: ws.isConnected,
    conversations: msgHooks.conversations,
    activeConversation: activeConversation,
    setActiveConversation: setActiveConversation,
    markConversationRead: markConversationRead,
    messages: msgHooks.messages,
    typingUsers: typingUsers,
    onlineUsers: onlineUsers,
    unreadTotal: msgHooks.unreadTotal,
    fetchConversations: msgHooks.fetchConversations,
    fetchMessages: msgHooks.fetchMessages,
    loadOlderMessages: msgHooks.loadOlderMessages,
    hasMoreOlder: msgHooks.hasMoreOlder,
    loadingOlder: msgHooks.loadingOlder,
    sendMessage: sendMessage,
    sendTyping: sendTyping,
    requestOnlineStatus: requestOnlineStatus,
    createConversation: msgHooks.createConversation,
    registerHandler: registerHandler,
    notificationPrefs: notificationPrefs,
    saveNotificationPrefs: saveNotificationPrefs,
    // Call manager (single source of truth for call state)
    callState: callManager.callState,
    setMediaMode: callManager.setMediaMode,
    initiateCall: callManager.initiateCall,
    acceptCall: callManager.acceptCall,
    rejectCall: callManager.rejectCall,
    endCall: callManager.endCall,
    markConnected: callManager.markConnected,
    inviteToCall: callManager.inviteToCall,
    clearPeersToOffer: callManager.clearPeersToOffer,
    sendWebRTCSignal: callManager.sendWebRTCSignal,
    // Internal — used by GlobalCallOverlay polling fallback to inject
    // an `incoming_call` event when the WS reconnect-replay missed it
    // (e.g. native Android app launched cold from an FCM push).
    _handleCallEvent: callManager.handleWsEvent,
  };

  return React.createElement(ChatContext.Provider, { value: value }, children);
}

export function useChat() {
  var context = useContext(ChatContext);
  if (!context) throw new Error('useChat must be used within a ChatProvider');
  return context;
}

export default ChatContext;
